/**
 * ActivityView - Unified activity log showing user + agent actions, tool call stats, doc reads
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle, Bot, Heart, ChevronDown, ChevronRight, Clock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { aiAPI } from '../services/api';

function formatTimeAgo(isoString: string | null): string {
  if (!isoString) return '';
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

function TodaySummary() {
  const today = new Date().toISOString().split('T')[0];
  const { data, isLoading, isError } = useQuery({
    queryKey: ['activity-summary', today],
    queryFn: () => aiAPI.getActivitySummary(today),
    refetchInterval: 60000,
  });

  if (isLoading) {
    return <div className="text-sm text-gray-400">Loading summary...</div>;
  }
  if (isError) {
    return <div className="text-sm text-red-500">Failed to load activity summary</div>;
  }
  if (!data) return null;

  const completedCount = data.user.completed_tasks.length;

  return (
    <div className="grid grid-cols-3 gap-3">
      <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center">
        <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mx-auto mb-1" />
        <div className="text-2xl font-bold text-green-700 dark:text-green-300">{completedCount}</div>
        <div className="text-xs text-green-600 dark:text-green-400">Tasks completed</div>
      </div>
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-center">
        <Bot className="w-5 h-5 text-blue-600 dark:text-blue-400 mx-auto mb-1" />
        <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{data.agent.total_tool_calls}</div>
        <div className="text-xs text-blue-600 dark:text-blue-400">Agent tool calls</div>
      </div>
      <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 text-center">
        <Heart className="w-5 h-5 text-purple-600 dark:text-purple-400 mx-auto mb-1" />
        <div className="text-2xl font-bold text-purple-700 dark:text-purple-300">{data.habits_completed}</div>
        <div className="text-xs text-purple-600 dark:text-purple-400">Habits done</div>
      </div>
    </div>
  );
}

function DocumentReads() {
  const [period, setPeriod] = useState(30);

  const { data, isLoading } = useQuery({
    queryKey: ['tool-call-stats', period],
    queryFn: () => aiAPI.getToolCallStats(period),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Document Read Counts</h3>
        <div className="flex gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setPeriod(d)}
              className={`px-2 py-0.5 text-xs rounded ${
                period === d
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="text-sm text-gray-400">Loading...</div>
      ) : !data?.document_reads?.length ? (
        <div className="text-sm text-gray-400 italic">No document reads in this period</div>
      ) : (
        <div className="space-y-1">
          {data.document_reads.map((doc) => (
            <div key={doc.doc_id} className="flex items-center justify-between text-sm py-1 px-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700/50">
              <span className="text-gray-700 dark:text-gray-300 truncate flex-1">
                {doc.title || doc.doc_id}
              </span>
              <span className="text-gray-500 dark:text-gray-400 ml-2 font-mono text-xs">
                {doc.read_count}x
              </span>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-gray-400 mt-2 italic">
        Counts reflect explicit get_document tool calls. Pinned docs are auto-injected and not counted here.
      </p>
    </div>
  );
}

function ToolCallDetails() {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['tool-call-log', 1],
    queryFn: () => aiAPI.getToolCallLog({ days: 1, limit: 50 }),
    enabled: expanded,
  });

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        Recent Tool Calls (Today)
      </button>

      {expanded && (
        <div className="mt-2 space-y-1 max-h-96 overflow-y-auto">
          {isLoading ? (
            <div className="text-sm text-gray-400">Loading...</div>
          ) : !data?.entries?.length ? (
            <div className="text-sm text-gray-400 italic">No tool calls today</div>
          ) : (
            data.entries.map((entry) => (
              <div
                key={entry.id}
                className="flex items-start gap-2 text-xs py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700"
              >
                {entry.success ? (
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{entry.tool_name}</span>
                    {entry.duration_ms != null && (
                      <span className="text-gray-400 flex items-center gap-0.5">
                        <Clock className="w-3 h-3" />
                        {entry.duration_ms}ms
                      </span>
                    )}
                    <span className="text-gray-400 ml-auto">{formatTimeAgo(entry.created_time)}</span>
                  </div>
                  {entry.arguments_json && (
                    <div className="text-gray-500 dark:text-gray-400 truncate mt-0.5">
                      {entry.arguments_json.length > 120
                        ? entry.arguments_json.substring(0, 120) + '...'
                        : entry.arguments_json}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function ToolCallSummary() {
  const { data, isLoading } = useQuery({
    queryKey: ['tool-call-stats', 30],
    queryFn: () => aiAPI.getToolCallStats(30),
  });

  if (isLoading) {
    return <div className="text-sm text-gray-400">Loading tool stats...</div>;
  }
  if (!data?.tool_summary?.length) return null;

  return (
    <div>
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
        Tool Usage (30 days) - {data.total_calls} total
      </h3>
      <div className="space-y-1">
        {data.tool_summary.slice(0, 15).map((tool) => {
          const pct = data.total_calls > 0 ? (tool.count / data.total_calls) * 100 : 0;
          const successRate = tool.count > 0 ? (tool.success_count / tool.count) * 100 : 100;
          return (
            <div key={tool.tool_name} className="flex items-center gap-2 text-sm">
              <span className="w-36 truncate text-gray-700 dark:text-gray-300 font-mono text-xs">
                {tool.tool_name}
              </span>
              <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
                <div
                  className={`h-full rounded ${successRate < 80 ? 'bg-yellow-400' : 'bg-blue-400'}`}
                  style={{ width: `${Math.max(pct, 2)}%` }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 w-12 text-right font-mono">
                {tool.count}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ActivityView() {
  return (
    <div className="h-full overflow-auto bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-xl font-bold text-gray-800 dark:text-gray-200 mb-1">Activity</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Your actions and agent tool calls, all in one place
          </p>
        </div>

        {/* Today's Summary */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">Today</h2>
          <TodaySummary />
        </section>

        {/* Document Read Counts */}
        <section className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <DocumentReads />
        </section>

        {/* Tool Usage Summary */}
        <section className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <ToolCallSummary />
        </section>

        {/* Tool Call Details (collapsible) */}
        <section className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
          <ToolCallDetails />
        </section>
      </div>
    </div>
  );
}
